import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useUserAuth } from '../../../context/UserAuthContext';

/**
 * Returns true for errors caused by network loss or request timeout —
 * the only cases where a retry makes sense.
 */
const isNetworkOrTimeoutError = (error) => {
  if (!error) return false;
  // Axios timeout
  if (error.code === 'ECONNABORTED') return true;
  // No response at all (network unreachable)
  if (error.code === 'ERR_NETWORK' || !error.response) return true;
  return false;
};

/**
 * Custom hook for managing automatic survey saving
 *
 * This hook provides:
 * - Debounced auto-save functionality
 * - Manual save triggering
 * - Save status tracking and feedback
 * - Error handling for save operations
 * - 60-second request timeout
 * - Automatic retry (up to 3 attempts, 30 s apart)
 *
 * @param {string} surveyId - Current survey ID
 * @param {Object} userInfo - User information including store details
 * @param {Object} frIDs - Form record IDs for tracking updates vs inserts
 * @param {Function} setFrIDs - Function to update form record IDs
 * @returns {Object} Auto-save state and functions
 */
export const useAutoSave = (surveyId, userInfo, frIDs, setFrIDs) => {
  const { user: firebaseUser } = useUserAuth();

  // Auto-save state tracking
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [isNetworkError, setIsNetworkError] = useState(false);

  // Refs for managing debouncing and cleanup
  const saveTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Track previous data to detect changes
  const previousDataRef = useRef(null);

  // Track files that have been successfully uploaded to prevent re-uploading
  const uploadedFilesRef = useRef(new Set());

  // Retry state
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const pendingRetryDataRef = useRef(null); // { formData, files, saveChangedOnly }

  // Configuration
  const DEBOUNCE_DELAY = 2000; // Wait 2 seconds after last change before saving
  const SAVE_STATUS_DISPLAY_TIME = 3000; // Show save status for 3 seconds
  const REQUEST_TIMEOUT = 60000; // 60-second request timeout
  const RETRY_DELAY = 30000; // 30 seconds between retries
  const MAX_RETRIES = 3;

  /**
   * Compares current form data with previous data to detect changes
   */
  const hasDataChanged = useCallback((currentData, previousData) => {
    if (!previousData) {
      return currentData && currentData.length > 0;
    }
    if (currentData.length !== previousData.length) {
      return true;
    }
    if (currentData.length === 0 && previousData.length === 0) {
      return false;
    }

    const currentMap = new Map();
    const previousMap = new Map();

    currentData.forEach((item) => {
      const valueKey = String(item.value);
      const explainKey = item.explain ? String(item.explain) : '';
      const responsibleKey = item.responsible ? String(item.responsible) : '';
      const compositeValue = `${valueKey}|${explainKey}|${responsibleKey}`;
      currentMap.set(item.question, compositeValue);
    });

    previousData.forEach((item) => {
      const valueKey = String(item.value);
      const explainKey = item.explain ? String(item.explain) : '';
      const responsibleKey = item.responsible ? String(item.responsible) : '';
      const compositeValue = `${valueKey}|${explainKey}|${responsibleKey}`;
      previousMap.set(item.question, compositeValue);
    });

    for (const [question, value] of currentMap) {
      if (previousMap.get(question) !== value) {
        return true;
      }
    }
    for (const question of previousMap.keys()) {
      if (!currentMap.has(question)) {
        return true;
      }
    }
    return false;
  }, []);

  /**
   * Cancels any pending retry timer.
   */
  const cancelRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    pendingRetryDataRef.current = null;
  }, []);

  /**
   * Performs the actual save operation to the backend
   *
   * @param {Array} formData - Array of form data objects {question, value}
   * @param {Array} files - Array of file objects to upload
   * @param {boolean} saveChangedOnly - If true, only save changed values
   * @param {boolean} _isRetry - Internal flag — true when called from the retry timer
   * @returns {Promise} Save operation promise
   */
  const performSave = useCallback(
    async (formData, files = [], saveChangedOnly = true, _isRetry = false) => {
      if (!surveyId || !userInfo.defaultStore || !firebaseUser?.email) {
        throw new Error('Missing required data for save operation');
      }

      // Cancel any ongoing save request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        setIsSaving(true);
        setSaveStatus('saving');
        setSaveError(null);
        setIsNetworkError(false);

        const token = await firebaseUser.getIdToken(true);

        // Filter for changed values only if requested
        let dataToSave = formData;
        if (saveChangedOnly && previousDataRef.current) {
          const previousMap = new Map();
          previousDataRef.current.forEach(item => {
            const valueKey = String(item.value);
            const explainKey = item.explain ? String(item.explain) : '';
            const responsibleKey = item.responsible ? String(item.responsible) : '';
            const compositeValue = `${valueKey}|${explainKey}|${responsibleKey}`;
            previousMap.set(item.question, compositeValue);
          });

          dataToSave = formData.filter(item => {
            const previousCompositeValue = previousMap.get(item.question);
            const currentValueKey = String(item.value);
            const currentExplainKey = item.explain ? String(item.explain) : '';
            const currentResponsibleKey = item.responsible ? String(item.responsible) : '';
            const currentCompositeValue = `${currentValueKey}|${currentExplainKey}|${currentResponsibleKey}`;

            if (previousCompositeValue === undefined) return true;
            if (previousCompositeValue !== currentCompositeValue) return true;
            return false;
          });
        }

        // Skip save if no changes to send
        if (dataToSave.length === 0 && files.length === 0) {
          setIsSaving(false);
          setSaveStatus('idle');
          return { savedRecords: [], savedFiles: [] };
        }

        // Create FormData for multipart upload
        const formDataForSubmission = new FormData();

        const dataArray = dataToSave
          .filter((item) => !item.question.endsWith('-files'))
          .map((item) => {
            const entry = {
              question: item.question,
              value: item.value,
            };
            if (item.explain !== undefined && item.explain !== null) {
              entry.explain = item.explain;
            }
            if (item.responsible !== undefined && item.responsible !== null) {
              entry.responsible = item.responsible;
            }
            if (frIDs[item.question]) {
              entry.frID = frIDs[item.question];
            }
            return entry;
          });

        dataArray.push({ storeId: userInfo.defaultStore });
        dataArray.push({ user: firebaseUser.email });
        dataArray.push({ surveyId });

        formDataForSubmission.append('data', JSON.stringify(dataArray));

        // Add file uploads — filter already-uploaded
        const newFiles = files.filter((file) => !uploadedFilesRef.current.has(file.name));
        if (newFiles.length > 0) {
          newFiles.forEach((file) => {
            formDataForSubmission.append('files', file);
          });
        }

        // Make the save request (with 60 s timeout)
        const response = await axios.post(
          '/hd-api/facilities-walkthrough/save',
          formDataForSubmission,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
            signal: abortControllerRef.current.signal,
            timeout: REQUEST_TIMEOUT,
          }
        );

        // --- Success: clear retry state ---
        cancelRetry();
        retryCountRef.current = 0;

        setLastSaveTime(new Date());
        setSaveStatus('saved');
        setIsNetworkError(false);

        previousDataRef.current = [...formData];

        if (newFiles.length > 0) {
          newFiles.forEach((file) => {
            uploadedFilesRef.current.add(file.name);
          });
        }

        // Update frIDs with returned record IDs
        if (response.data?.data?.savedRecords) {
          const savedRecords = response.data.data.savedRecords;
          if (Array.isArray(savedRecords) && savedRecords.length > 0) {
            const newFrIDs = {};
            savedRecords.forEach(record => {
              if (record.frID && record.formName) {
                newFrIDs[record.formName] = record.frID;
              }
            });
            if (setFrIDs && Object.keys(newFrIDs).length > 0) {
              setFrIDs(prevFrIDs => ({ ...prevFrIDs, ...newFrIDs }));
            }
          }
        }

        // Hide save status after delay
        setTimeout(() => {
          setSaveStatus('idle');
        }, SAVE_STATUS_DISPLAY_TIME);

        return response.data;
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          setSaveStatus('idle');
          return;
        }

        const networkTimeout = isNetworkOrTimeoutError(error);
        setIsNetworkError(networkTimeout);

        if (networkTimeout) {
          setSaveError(
            'Unable to reach the server. Please check your connection — the app will retry automatically.'
          );
          setSaveStatus('error');

          // Schedule retry if under the cap and this isn't already a capped-out retry
          if (retryCountRef.current < MAX_RETRIES) {
            pendingRetryDataRef.current = { formData, files, saveChangedOnly };
            retryTimerRef.current = setTimeout(() => {
              retryCountRef.current += 1;
              const rd = pendingRetryDataRef.current;
              if (rd) {
                performSave(rd.formData, rd.files, rd.saveChangedOnly, true).catch(() => {
                  // handled inside performSave
                });
              }
            }, RETRY_DELAY);
          }
        } else {
          // Non-network error (validation, auth, etc.) — fail immediately
          setSaveError(error.message || 'Failed to save survey');
          setSaveStatus('error');
        }

        throw error;
      } finally {
        setIsSaving(false);
        abortControllerRef.current = null;
      }
    },
    [surveyId, userInfo.defaultStore, firebaseUser, frIDs, setFrIDs, cancelRetry]
  );

  /**
   * Schedules an auto-save operation with debouncing
   */
  const scheduleAutoSave = useCallback(
    (formData, files = []) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (!formData || formData.length === 0) return;
      if (!hasDataChanged(formData, previousDataRef.current)) return;

      // A new manual change cancels any pending retry
      cancelRetry();
      retryCountRef.current = 0;

      saveTimeoutRef.current = setTimeout(() => {
        performSave(formData, files).catch(() => {});
      }, DEBOUNCE_DELAY);
    },
    [performSave, hasDataChanged, cancelRetry]
  );

  /**
   * Performs an immediate save operation (no debouncing)
   */
  const saveImmediately = useCallback(
    async (formData, files = [], forceRun = false, saveChangedOnly = true) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // A new manual save cancels any pending retry
      cancelRetry();
      retryCountRef.current = 0;

      if (forceRun && !saveChangedOnly) {
        return performSave(formData, files, false);
      }
      if (forceRun && saveChangedOnly) {
        return performSave(formData, files, true);
      }
      if (!forceRun && !hasDataChanged(formData, previousDataRef.current)) {
        return null;
      }
      return performSave(formData, files, saveChangedOnly);
    },
    [performSave, hasDataChanged, cancelRetry]
  );

  /**
   * Cancels any pending save operations
   */
  const cancelPendingSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    cancelRetry();
  }, [cancelRetry]);

  /**
   * Clears any save error state
   */
  const clearSaveError = useCallback(() => {
    setSaveError(null);
    setIsNetworkError(false);
    if (saveStatus === 'error') {
      setSaveStatus('idle');
    }
  }, [saveStatus]);

  /**
   * Gets a human-readable save status message
   */
  const getSaveStatusMessage = useCallback(() => {
    switch (saveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return `Saved at ${lastSaveTime?.toLocaleTimeString()}`;
      case 'error':
        return `Save failed: ${saveError}`;
      default:
        return '';
    }
  }, [saveStatus, lastSaveTime, saveError]);

  /**
   * Initializes the previous data reference with current form data
   */
  const initializePreviousData = useCallback((formData) => {
    previousDataRef.current = formData ? [...formData] : null;
    uploadedFilesRef.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelPendingSave();
    };
  }, [cancelPendingSave]);

  return {
    // State
    isSaving,
    lastSaveTime,
    saveError,
    saveStatus,
    isNetworkError,

    // Functions
    scheduleAutoSave,
    saveImmediately,
    cancelPendingSave,
    clearSaveError,
    getSaveStatusMessage,
    initializePreviousData,

    // Computed properties
    isAutoSaveEnabled: !!(
      surveyId &&
      userInfo.defaultStore &&
      firebaseUser?.email
    ),
    hasRecentSave: lastSaveTime && Date.now() - lastSaveTime.getTime() < 60000,
  };
};
